import { Controller } from "@hotwired/stimulus"
import { positionDropdownMenu } from "./dropdown_positioning"

// Left/right arrow buttons for a horizontally-scrollable sub-nav (application.html.erb: the org,
// project, and admin ones each wire this up independently, only one ever rendered on a given
// page). Driven by actual overflow, not a screen-size breakpoint: a plain CSS media query can't
// tell "these 7 items fit at 1400px but not at 900px" from "these 9 items never fit at all,"
// and a narrow desktop window needs the same arrows a phone does. Touch/trackpad drag-scrolling
// the row directly keeps working the whole time this is active; the arrows are an additional way
// to move it, not a replacement, so nothing here ever disables native scrolling.
export default class extends Controller {
  static targets = ["scroller", "prev", "next"]

  connect() {
    this.update = this.update.bind(this)
    this.update()
    this.revealActive()
    window.addEventListener("resize", this.update)

    // The project sub-nav's own Alerting/Monitoring dropdowns live inside this same scroller,
    // and got clipped the moment their menu opened while the row was actually overflowing; see
    // dropdown_positioning.js for why and how this positions each menu by hand instead.
    this.element.querySelectorAll(".dropdown").forEach((dropdown) => {
      dropdown.addEventListener("show.bs.dropdown", () => this.positionMenu(dropdown))
    })
  }

  disconnect() {
    window.removeEventListener("resize", this.update)
  }

  scrollPrev() {
    this.scrollerTarget.scrollBy({ left: -160, behavior: "smooth" })
  }

  scrollNext() {
    this.scrollerTarget.scrollBy({ left: 160, behavior: "smooth" })
  }

  // The current page's own pill (or dropdown toggle) can sit past either edge of an overflowing
  // row: Settings, last in the project sub-nav, rendered as a sliver behind the right arrow on a
  // 1280px screen, so the one item that says where you are was the one you couldn't see. Centered
  // in the row on load, instantly rather than smoothly, since this is where the page starts, not
  // a movement the reader asked for. Measured twice: scrolling shows the left arrow, which
  // narrows the row by the arrow's own width and can push the last pill back under the right
  // one, so the second pass corrects for the row's new size.
  revealActive() {
    const el = this.scrollerTarget
    const active = el.querySelector(".nav-link.active")
    if (!active) return

    for (let pass = 0; pass < 2; pass++) {
      if (!el.classList.contains("sub-nav-pills--overflowing")) return

      const row = el.getBoundingClientRect()
      const pill = active.getBoundingClientRect()
      if (pill.left >= row.left && pill.right <= row.right) return

      el.scrollLeft += (pill.left + pill.width / 2) - (row.left + row.width / 2)
      this.update()
    }
  }

  // Also wired to the scroller's own native "scroll" event (data-action in the markup), so a
  // touch swipe or trackpad drag keeps the arrows' visibility in sync with wherever it actually
  // left the row, not just wherever the last arrow click put it.
  update() {
    const el = this.scrollerTarget
    const overflowing = el.scrollWidth > el.clientWidth + 1

    el.classList.toggle("sub-nav-pills--overflowing", overflowing)
    this.prevTarget.hidden = !overflowing || el.scrollLeft <= 0
    this.nextTarget.hidden = !overflowing || el.scrollLeft >= el.scrollWidth - el.clientWidth - 1
  }

  // See dropdown_positioning.js for why this needs position: fixed rather than a plain CSS
  // override, and why Bootstrap's own popperConfig can't be used to get there either. Shared
  // with custom_select_controller, which has this exact same clipped-ancestor problem for its
  // own dropdown-style replacement for a native <select>.
  positionMenu(dropdown) {
    const toggle = dropdown.querySelector('[data-bs-toggle="dropdown"]')
    const menu = dropdown.querySelector(".dropdown-menu")

    positionDropdownMenu(toggle, menu)
  }
}
